import {
  parseGameplayWave,
  serialiseGameplayWave,
  type GameplayWaveRecipeSection,
} from '../gameplay/GameplayWaveRecipe.js';
import { type BlendMode, isBlendMode } from '../core/VisualRenderer.js';
import { isMutationAction } from '../core/VisualMutation.js';
import { isVisualSignalName } from '../signals/VisualSignal.js';
import { isSpatialMode, type SpatialMode } from '../wave/SpatialPlanner.js';
import {
  DEFAULT_WAVE_WEIGHTS,
  isWaveMode,
  type WaveMode,
  type WaveStyleWeights,
} from '../wave/WaveDirector.js';
import type { SignalMapping, SignalMappingValue, VisualLayerConfig } from '../layers/VisualLayer.js';

/**
 * The whole visual configuration, as a value.
 *
 * A recipe is the artwork's recipe in the literal sense: it says nothing about
 * what a run produced and everything about how to produce one. Combined with a
 * seed and an input timeline it should regenerate a piece; on its own it is a
 * style that can be applied to any run.
 *
 * `version` exists because this file will change and saved recipes must not
 * become unreadable when it does. `parse` accepts anything at or below the
 * version it knows, fills in what is missing, and rejects what it cannot make
 * sense of — a recipe from a user's disk is untrusted input like any other.
 */

/**
 * 2 adds the wave and spatial settings.
 *
 * Bumped rather than reused because a recipe saved by this build says something
 * a version 1 reader would silently ignore, and a picture that quietly loses its
 * placement rules is worse than one that refuses to load. Version 1 recipes still
 * read: the three fields are optional and `resolveWaveSettings` supplies the
 * defaults, which are exactly the behaviour version 1 had.
 */
export const VISUAL_RECIPE_VERSION = 2;

export const RENDER_MODES = ['FIXED', 'SWITCH', 'RANDOM', 'AUTO'] as const;
export type RenderMode = (typeof RENDER_MODES)[number];

export function isRenderMode(value: string): value is RenderMode {
  return (RENDER_MODES as readonly string[]).includes(value);
}

export interface VisualRecipe {
  readonly version: number;
  readonly seed: number;
  readonly renderMode: RenderMode;
  /**
   * How a wave picks its material. Optional, so version 1 recipes still parse.
   *
   * Read through `resolveWaveSettings` rather than directly, so that what an
   * absent field means is decided in one place instead of at every call site.
   */
  readonly waveMode?: WaveMode;
  /** Where a wave is placed. Optional, for the same reason. */
  readonly spatialMode?: SpatialMode;
  /** Relative weights by renderer id, for `WEIGHTED_RANDOM`. */
  readonly waveStyles?: WaveStyleWeights;
  /** How many materials `COLLISION` may send at one disturbance. */
  readonly collision?: CollisionSettings;
  /** How the persistent paint behaves. Ignored unless a layer draws it. */
  readonly inkPaint?: InkPaintSettings;
  /**
   * How the waves the player taps are drawn. Optional, and its absence means
   * MANDALA — see `GameplayWaveRecipe.ts`.
   *
   * Separate from `waveMode`/`waveStyles` on purpose: those describe what the
   * artwork emits, this describes the gameplay object. A background of smoke
   * must not force smoke waves at the player.
   */
  readonly gameplayWave?: GameplayWaveRecipeSection;
  readonly layers: readonly VisualLayerConfig[];
}

export interface CollisionSettings {
  readonly minMaterials: number;
  readonly maxMaterials: number;
}

export interface InkPaintSettings {
  /** Whether the paint layer should be built at all. */
  readonly enabled: boolean;
  /** 1 keeps a stain for the whole run; below that it fades. */
  readonly persistence: number;
  /** How fast a wet splat sets and becomes part of the surface. */
  readonly dryingRate: number;
  /** How far a wet edge runs into the surface. */
  readonly bleedRate: number;
}

export const COLLISION_DEFAULTS: CollisionSettings = { minMaterials: 1, maxMaterials: 3 };
export const INK_PAINT_DEFAULTS: InkPaintSettings = {
  // Off unless asked for, like every other Phase 6 addition: a recipe that
  // never mentioned paint must not start accumulating it.
  enabled: false,
  persistence: 0.85,
  dryingRate: 0.4,
  bleedRate: 0.6,
};

/** Everything the wave side needs, with every absent field filled in. */
export interface WaveSettings {
  /**
   * Whether this recipe asked for generative waves at all.
   *
   * True when the recipe names any of the three fields. Waves are opt-in
   * because turning them on for every existing recipe would change what those
   * recipes draw: the renderers already spawn from signals themselves, so a
   * recipe that never asked for a wave would suddenly get two marks per note.
   */
  readonly enabled: boolean;
  readonly waveMode: WaveMode;
  readonly spatialMode: SpatialMode;
  readonly waveStyles: WaveStyleWeights;
  readonly collision: CollisionSettings;
  readonly inkPaint: InkPaintSettings;
}

/**
 * The wave settings a recipe is asking for.
 *
 * The defaults are version 1's behaviour on purpose: `FIXED` keeps a wave in the
 * material its own layer already was, and `CENTER` keeps it where it already
 * appeared. A recipe written before any of this existed therefore draws what it
 * always drew, and generative placement is something a recipe opts into rather
 * than something that happens to every old recipe on upgrade.
 */
export function resolveWaveSettings(recipe: VisualRecipe | null): WaveSettings {
  return {
    enabled:
      recipe !== null &&
      (recipe.waveMode !== undefined ||
        recipe.spatialMode !== undefined ||
        recipe.waveStyles !== undefined ||
        recipe.collision !== undefined),
    waveMode: recipe?.waveMode ?? 'FIXED',
    spatialMode: recipe?.spatialMode ?? 'CENTER',
    waveStyles: recipe?.waveStyles ?? DEFAULT_WAVE_WEIGHTS,
    collision: {
      ...COLLISION_DEFAULTS,
      ...(recipe?.collision ?? {}),
    },
    inkPaint: {
      ...INK_PAINT_DEFAULTS,
      ...(recipe?.inkPaint ?? {}),
    },
  };
}

export const LAYER_DEFAULTS = {
  enabled: true,
  opacity: 1,
  blendMode: 'normal' as BlendMode,
  intensity: 1,
  audioReactivity: 1,
  zDepth: 0,
} as const;

/** The recipe a session starts with: the game as it was before any of this. */
export function defaultRecipe(seed: number): VisualRecipe {
  return {
    version: VISUAL_RECIPE_VERSION,
    seed,
    renderMode: 'FIXED',
    layers: [layer({ id: 'mandala', renderer: 'MANDALA', seed })],
  };
}

/** Builds a layer, filling in every default so the rest of the engine need not. */
export function layer(config: Partial<VisualLayerConfig> & { renderer: string }): VisualLayerConfig {
  return {
    id: config.id ?? `${config.renderer.toLowerCase()}-${config.zDepth ?? 0}`,
    renderer: config.renderer,
    enabled: config.enabled ?? LAYER_DEFAULTS.enabled,
    opacity: config.opacity ?? LAYER_DEFAULTS.opacity,
    blendMode: config.blendMode ?? LAYER_DEFAULTS.blendMode,
    intensity: config.intensity ?? LAYER_DEFAULTS.intensity,
    seed: config.seed ?? 0,
    audioReactivity: config.audioReactivity ?? LAYER_DEFAULTS.audioReactivity,
    zDepth: config.zDepth ?? LAYER_DEFAULTS.zDepth,
    parameters: config.parameters ?? {},
    signalMapping: config.signalMapping ?? {},
  };
}

export interface RecipeParseResult {
  readonly recipe: VisualRecipe | null;
  /** What was wrong, or what had to be repaired. Empty on a clean parse. */
  readonly problems: readonly string[];
}

/**
 * Reads a recipe from untrusted JSON.
 *
 * Repairs rather than rejects wherever a sensible value exists — a recipe with
 * one unreadable layer is worth more than no recipe — and reports every repair,
 * so a caller that wants to be strict can look at `problems` and refuse.
 */
export function parseRecipe(input: unknown): RecipeParseResult {
  const problems: string[] = [];
  if (typeof input !== 'object' || input === null) {
    return { recipe: null, problems: ['not an object'] };
  }
  const raw = input as Record<string, unknown>;

  const version = typeof raw.version === 'number' ? raw.version : 0;
  if (version <= 0) problems.push('missing version');
  if (version > VISUAL_RECIPE_VERSION) {
    return {
      recipe: null,
      problems: [`recipe version ${version} is newer than this build understands`],
    };
  }

  const seed = typeof raw.seed === 'number' && Number.isFinite(raw.seed) ? raw.seed | 0 : 0;
  if (typeof raw.seed !== 'number') problems.push('missing seed; defaulted to 0');

  const renderMode =
    typeof raw.renderMode === 'string' && isRenderMode(raw.renderMode) ? raw.renderMode : 'FIXED';
  if (raw.renderMode !== undefined && renderMode !== raw.renderMode) {
    problems.push(`unknown renderMode "${String(raw.renderMode)}"; using FIXED`);
  }

  const waveMode =
    typeof raw.waveMode === 'string' && isWaveMode(raw.waveMode) ? raw.waveMode : undefined;
  if (raw.waveMode !== undefined && waveMode === undefined) {
    problems.push(`unknown waveMode "${String(raw.waveMode)}"; using the default`);
  }
  const spatialMode =
    typeof raw.spatialMode === 'string' && isSpatialMode(raw.spatialMode)
      ? raw.spatialMode
      : undefined;
  if (raw.spatialMode !== undefined && spatialMode === undefined) {
    problems.push(`unknown spatialMode "${String(raw.spatialMode)}"; using the default`);
  }
  const waveStyles = weights(raw.waveStyles, problems);
  const collision = collisionOf(raw.collision, problems);
  const inkPaint = inkPaintOf(raw.inkPaint, problems);
  // Carried through verbatim when present and omitted when not, so an older
  // recipe stays byte-identical on a save/load round trip and keeps playing as
  // MANDALA. The meaning of the section is decided by `parseGameplayWave` at
  // the point of use, not here.
  const gameplayWave = gameplayWaveOf(raw.gameplayWave, problems);

  const layers: VisualLayerConfig[] = [];
  const rawLayers = Array.isArray(raw.layers) ? raw.layers : [];
  if (!Array.isArray(raw.layers)) problems.push('layers missing; recipe has no layers');

  rawLayers.forEach((entry, index) => {
    if (typeof entry !== 'object' || entry === null) {
      problems.push(`layer ${index} is not an object; skipped`);
      return;
    }
    const source = entry as Record<string, unknown>;
    const renderer = typeof source.renderer === 'string' ? source.renderer : null;
    if (renderer === null || renderer.length === 0) {
      problems.push(`layer ${index} names no renderer; skipped`);
      return;
    }
    const blend =
      typeof source.blendMode === 'string' && isBlendMode(source.blendMode)
        ? source.blendMode
        : LAYER_DEFAULTS.blendMode;
    if (source.blendMode !== undefined && blend !== source.blendMode) {
      problems.push(`layer ${index} has unknown blendMode "${String(source.blendMode)}"`);
    }

    layers.push(
      layer({
        renderer,
        ...(typeof source.id === 'string' && source.id.length > 0 ? { id: source.id } : {}),
        enabled: source.enabled !== false,
        opacity: number(source.opacity, LAYER_DEFAULTS.opacity, 0, 1),
        blendMode: blend,
        intensity: number(source.intensity, LAYER_DEFAULTS.intensity, 0, 8),
        seed: typeof source.seed === 'number' ? source.seed | 0 : seed + index,
        audioReactivity: number(source.audioReactivity, LAYER_DEFAULTS.audioReactivity, 0, 4),
        zDepth: typeof source.zDepth === 'number' ? source.zDepth : index,
        parameters: parameters(source.parameters),
        signalMapping: mapping(source.signalMapping, index, problems),
      }),
    );
  });

  return {
    recipe: {
      version: VISUAL_RECIPE_VERSION,
      seed,
      renderMode,
      ...(waveMode === undefined ? {} : { waveMode }),
      ...(spatialMode === undefined ? {} : { spatialMode }),
      ...(waveStyles === undefined ? {} : { waveStyles }),
      ...(collision === undefined ? {} : { collision }),
      ...(inkPaint === undefined ? {} : { inkPaint }),
      ...(gameplayWave === undefined ? {} : { gameplayWave }),
      layers,
    },
    problems,
  };
}

/** JSON, stable key order, ready to write to disk or to a Song Pack. */
export function serializeRecipe(recipe: VisualRecipe): string {
  return JSON.stringify(
    {
      version: recipe.version,
      seed: recipe.seed,
      renderMode: recipe.renderMode,
      // Written only when the recipe actually carries them, because their
      // *absence* is itself the instruction: a recipe that names none of the
      // three is saying it does not use generative waves, and the engine reads
      // it that way. Writing the resolved defaults instead would mean a recipe
      // that never asked for waves came back from disk with them switched on —
      // saving a picture would change it, which is the one thing saving must
      // not do.
      ...(recipe.waveMode === undefined ? {} : { waveMode: recipe.waveMode }),
      ...(recipe.spatialMode === undefined ? {} : { spatialMode: recipe.spatialMode }),
      ...(recipe.waveStyles === undefined ? {} : { waveStyles: recipe.waveStyles }),
      ...(recipe.collision === undefined ? {} : { collision: recipe.collision }),
      ...(recipe.inkPaint === undefined ? {} : { inkPaint: recipe.inkPaint }),
      ...(recipe.gameplayWave === undefined ? {} : { gameplayWave: recipe.gameplayWave }),
      layers: recipe.layers.map((entry) => ({
        id: entry.id,
        renderer: entry.renderer,
        enabled: entry.enabled,
        opacity: entry.opacity,
        blendMode: entry.blendMode,
        intensity: entry.intensity,
        seed: entry.seed,
        audioReactivity: entry.audioReactivity,
        zDepth: entry.zDepth,
        parameters: entry.parameters,
        signalMapping: entry.signalMapping,
      })),
    },
    null,
    2,
  );
}

function number(value: unknown, fallback: number, min: number, max: number): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) return fallback;
  return value < min ? min : value > max ? max : value;
}

function collisionOf(value: unknown, problems: string[]): CollisionSettings | undefined {
  if (value === undefined) return undefined;
  if (typeof value !== 'object' || value === null) {
    problems.push('collision is not an object; using the defaults');
    return undefined;
  }
  const raw = value as Record<string, unknown>;
  const min = Math.round(number(raw.minMaterials, COLLISION_DEFAULTS.minMaterials, 1, 6));
  const max = Math.round(number(raw.maxMaterials, COLLISION_DEFAULTS.maxMaterials, 1, 6));
  if (max < min) {
    problems.push(`collision.maxMaterials ${max} is below minMaterials ${min}; raised`);
    return { minMaterials: min, maxMaterials: min };
  }
  return { minMaterials: min, maxMaterials: max };
}

/**
 * Validates the gameplay wave section without resolving it.
 *
 * Anything unrecognised is dropped with a note rather than rejected: a recipe
 * from a later build should still open here and play as MANDALA, which is what
 * an absent or unusable section means.
 */
function gameplayWaveOf(
  value: unknown,
  problems: string[],
): GameplayWaveRecipeSection | undefined {
  if (value === undefined || value === null) return undefined;
  if (typeof value !== 'object') {
    problems.push('gameplayWave is not an object; gameplay waves stay MANDALA');
    return undefined;
  }
  const parsed = parseGameplayWave(value);
  for (const problem of parsed.problems) problems.push(problem);
  return serialiseGameplayWave(parsed.settings);
}

function inkPaintOf(value: unknown, problems: string[]): InkPaintSettings | undefined {
  if (value === undefined) return undefined;
  if (typeof value !== 'object' || value === null) {
    problems.push('inkPaint is not an object; using the defaults');
    return undefined;
  }
  const raw = value as Record<string, unknown>;
  return {
    enabled: raw.enabled === true,
    persistence: number(raw.persistence, INK_PAINT_DEFAULTS.persistence, 0, 1),
    dryingRate: number(raw.dryingRate, INK_PAINT_DEFAULTS.dryingRate, 0.02, 8),
    bleedRate: number(raw.bleedRate, INK_PAINT_DEFAULTS.bleedRate, 0, 3),
  };
}

/** A weighting table. Renderer ids are not validated here — see `WaveDirector`. */
function weights(value: unknown, problems: string[]): WaveStyleWeights | undefined {
  if (value === undefined) return undefined;
  if (typeof value !== 'object' || value === null) {
    problems.push('waveStyles is not an object; using the default weights');
    return undefined;
  }
  const out: Record<string, number> = {};
  for (const [key, item] of Object.entries(value as Record<string, unknown>)) {
    if (typeof item !== 'number' || !Number.isFinite(item)) {
      problems.push(`waveStyles["${key}"] is not a number; ignored`);
      continue;
    }
    out[key] = item < 0 ? 0 : item;
  }
  return Object.keys(out).length === 0 ? undefined : out;
}

function parameters(value: unknown): Record<string, number | string | boolean> {
  if (typeof value !== 'object' || value === null) return {};
  const out: Record<string, number | string | boolean> = {};
  for (const [key, item] of Object.entries(value as Record<string, unknown>)) {
    if (typeof item === 'number' && Number.isFinite(item)) out[key] = item;
    else if (typeof item === 'string' || typeof item === 'boolean') out[key] = item;
  }
  return out;
}

function mapping(value: unknown, index: number, problems: string[]): SignalMapping {
  if (typeof value !== 'object' || value === null) return {};
  const out: Record<string, SignalMappingValue> = {};
  for (const [key, item] of Object.entries(value as Record<string, unknown>)) {
    if (!isVisualSignalName(key)) {
      problems.push(`layer ${index} maps unknown signal "${key}"; ignored`);
      continue;
    }
    if (typeof item === 'string' && item.length > 0) {
      out[key] = item;
      continue;
    }
    if (typeof item === 'object' && item !== null) {
      const entry = item as Record<string, unknown>;

      // Phase 3's action form. Checked before `target` so a mapping carrying
      // both is read as a mutation, which is the more specific instruction.
      if (typeof entry.action === 'string') {
        if (!isMutationAction(entry.action)) {
          problems.push(`layer ${index} maps "${key}" to unknown action "${entry.action}"; ignored`);
          continue;
        }
        out[key] = {
          action: entry.action,
          ...(typeof entry.strength === 'number' ? { strength: entry.strength } : {}),
          ...(typeof entry.scale === 'number' ? { scale: entry.scale } : {}),
          ...(entry.curve === 'sqrt' || entry.curve === 'square' || entry.curve === 'log'
            ? { curve: entry.curve }
            : {}),
        };
        continue;
      }

      if (typeof entry.target === 'string' && entry.target.length > 0) {
        out[key] = {
          target: entry.target,
          ...(typeof entry.scale === 'number' ? { scale: entry.scale } : {}),
          ...(typeof entry.offset === 'number' ? { offset: entry.offset } : {}),
          ...(entry.curve === 'sqrt' || entry.curve === 'square' || entry.curve === 'log'
            ? { curve: entry.curve }
            : {}),
          ...(Array.isArray(entry.clamp) &&
          entry.clamp.length === 2 &&
          typeof entry.clamp[0] === 'number' &&
          typeof entry.clamp[1] === 'number'
            ? { clamp: [entry.clamp[0], entry.clamp[1]] as const }
            : {}),
        };
        continue;
      }
    }
    problems.push(`layer ${index} has an unreadable mapping for "${key}"; ignored`);
  }
  return out as SignalMapping;
}
